import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardMedia,
  CardContent,
  OutlinedInput,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, orderBy, addDoc, updateDoc, deleteDoc, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { db, functions, functionsUS } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Product, ProductCategory, ProductOption, Staff } from '../types';
import MediaUpload from '../components/MediaUpload';
import { getLocalizedName, getLocalizedDescription } from '../utils/localization';

import { clamp, pctToRate } from '../utils/numberGuard';
const Products: React.FC = () => {
  const { shop } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [staffMembers, setStaffMembers] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<string>('');
  const [migrating, setMigrating] = useState(false);
  const [showContinueDialog, setShowContinueDialog] = useState(false);
  const [lastSavedCategoryId, setLastSavedCategoryId] = useState<string>('');
  const [formData, setFormData] = useState<{
    categoryId: string;
    code: string;
    name: string;
    nameEn: string;
    nameTh: string;
    nameZhTw: string;
    nameKo: string;
    description: string;
    descriptionEn: string;
    descriptionTh: string;
    descriptionZhTw: string;
    descriptionKo: string;
    price: number | string;
    imageUrl: string;
    mediaType: 'image' | 'video';
    optionIds: string[];
    printerIds: string[];
    displayStatus: 'available' | 'hidden' | 'soldout';
    availableTimeSlots: {
      type: 'always' | 'specific_times';
      timeSlots?: {
        start: string;
        end: string;
        name?: string;
      }[];
    };
    allergens: string[];
    isSpicy: boolean;
    isVegetarian: boolean;
    isAskPrice: boolean;
    costSettings: {
      hasCost: boolean;
      cost: number;
      backRate: number;
    };
    isActive: boolean;
    showOnReservationMenu: boolean;
    assignedStaffIds: string[];
    deliveryAvailable: boolean;
    deliveryPrice: number | string;
  }>({
    categoryId: '',
    code: '',
    name: '',
    nameEn: '',
    nameTh: '',
    nameZhTw: '',
    nameKo: '',
    description: '',
    descriptionEn: '',
    descriptionTh: '',
    descriptionZhTw: '',
    descriptionKo: '',
    price: '',
    imageUrl: '',
    mediaType: 'image' as 'image' | 'video',
    optionIds: [],
    printerIds: [],
    displayStatus: 'available',
    availableTimeSlots: {
      type: 'always',
      timeSlots: [],
    },
    allergens: [],
    isSpicy: false,
    isVegetarian: false,
    isAskPrice: false,
    costSettings: {
      hasCost: false,
      cost: 0,
      backRate: 0.5,
    },
    isActive: true,
    showOnReservationMenu: false,
    assignedStaffIds: [],
    deliveryAvailable: false,
    deliveryPrice: '',
  });

  useEffect(() => {
    if (shop) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [shop]);

  const fetchData = async () => {
    if (!shop) return;

    try {
      // 商品取得
      const productsQuery = query(
        collection(db, 'products'),
        where('shopId', '==', shop.id)
      );
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Product[];
      // クライアント側でソート（sortOrderの昇順）
      productsData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setProducts(productsData);

      // カテゴリ取得
      const categoriesQuery = query(
        collection(db, 'productCategories'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as ProductCategory[];
      // クライアント側でソート
      categoriesData.sort((a, b) => a.sortOrder - b.sortOrder);
      setCategories(categoriesData);

      // オプション取得
      const optionsQuery = query(
        collection(db, 'productOptions'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const optionsSnapshot = await getDocs(optionsQuery);
      const optionsData = optionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as ProductOption[];
      setOptions(optionsData);

      // スタッフ取得
      const staffQuery = query(
        collection(db, 'staff'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const staffSnapshot = await getDocs(staffQuery);
      const staffData = staffSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Staff[];
      // ソート順でソート
      staffData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setStaffMembers(staffData);
    } catch (error) {
      console.error('データ取得エラー:', error);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        categoryId: String(product.categoryId || ''),
        code: product.code || '',
        name: product.name,
        nameEn: product.nameEn || '',
        nameTh: product.nameTh || '',
        nameZhTw: product.nameZh || product.nameZhTw || '',  // nameZh優先（旧データ互換）
        nameKo: product.nameKo || '',
        description: product.description || '',
        descriptionEn: product.descriptionEn || '',
        descriptionTh: product.descriptionTh || '',
        descriptionZhTw: product.descriptionZh || product.descriptionZhTw || '',  // nameZh優先
        descriptionKo: product.descriptionKo || '',
        price: product.price,
        imageUrl: product.imageUrl || '',
        mediaType: product.mediaType || 'image',
        optionIds: product.optionIds || [],
        printerIds: product.printerIds || [],
        displayStatus: product.displayStatus || 'available',
        availableTimeSlots: product.availableTimeSlots || {
          type: 'always',
          timeSlots: [],
        },
        allergens: product.allergens || [],
        isSpicy: product.isSpicy || false,
        isVegetarian: product.isVegetarian || false,
        isAskPrice: product.isAskPrice || false,
        costSettings: product.costSettings || {
          hasCost: false,
          cost: 0,
          backRate: shop?.costBackSettings?.defaultBackRate || 0.5,
        },
        isActive: product.isActive,
        showOnReservationMenu: product.showOnReservationMenu || false,
        assignedStaffIds: product.assignedStaffIds || [],
        deliveryAvailable: product.deliveryAvailable || false,
        deliveryPrice: product.deliveryPrice || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        categoryId: categories[0]?.id || '',
        code: '',
        name: '',
        nameEn: '',
        nameTh: '',
        nameZhTw: '',
        nameKo: '',
        description: '',
        descriptionEn: '',
        descriptionTh: '',
        descriptionZhTw: '',
        descriptionKo: '',
        price: '',
        imageUrl: '',
        mediaType: 'image' as 'image' | 'video',
        optionIds: [],
        printerIds: [],
        displayStatus: 'available',
        availableTimeSlots: {
          type: 'always',
          timeSlots: [],
        },
        allergens: [],
        isSpicy: false,
        isVegetarian: false,
        isAskPrice: false,
        costSettings: {
          hasCost: false,
          cost: 0,
          backRate: shop?.costBackSettings?.defaultBackRate || 0.5,
        },
        isActive: true,
        showOnReservationMenu: false,
        assignedStaffIds: [],
        deliveryAvailable: false,
        deliveryPrice: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setError(null);
  };

  // 商品コードの自動採番（カテゴリ内で最大番号+1）
  const generateProductCode = (categoryId: string): string => {
    const categoryProducts = products.filter(p => p.categoryId === categoryId);
    const codes = categoryProducts
      .map(p => p.code)
      .filter(code => code && /^\d+$/.test(code))
      .map(code => parseInt(code!, 10));

    const maxCode = codes.length > 0 ? Math.max(...codes) : 0;
    return String(maxCode + 1).padStart(3, '0');
  };

  const handleSubmit = async () => {
    if (!shop || !formData.name || !formData.categoryId) {
      setError('商品名とカテゴリは必須です');
      return;
    }

    try {
      const now = Timestamp.now();

      // availableTimeSlotsのクリーンアップ
      const cleanedTimeSlots = formData.availableTimeSlots.type === 'specific_times' &&
                               formData.availableTimeSlots.timeSlots &&
                               formData.availableTimeSlots.timeSlots.length > 0
        ? {
            type: 'specific_times' as const,
            timeSlots: formData.availableTimeSlots.timeSlots.filter(slot =>
              slot.start && slot.end
            ),
          }
        : { type: 'always' as const };

      // 商品コード：空の場合は自動採番
      const productCode = formData.code.trim() || generateProductCode(formData.categoryId);

      const productData = {
        shopId: shop.id,
        categoryId: formData.categoryId,
        code: productCode,
        name: formData.name,
        nameEn: formData.nameEn || null,
        nameTh: formData.nameTh || null,
        nameZh: formData.nameZhTw || null,  // nameZhで統一
        nameKo: formData.nameKo || null,
        description: formData.description || null,
        descriptionEn: formData.descriptionEn || null,
        descriptionTh: formData.descriptionTh || null,
        descriptionZh: formData.descriptionZhTw || null,  // nameZhで統一
        descriptionKo: formData.descriptionKo || null,
        // 負の価格は会計で値引きとして通る。
        price: clamp(formData.price, 0, Number.MAX_SAFE_INTEGER),
        imageUrl: formData.imageUrl || null,
        mediaType: formData.mediaType || null,
        optionIds: formData.optionIds,
        printerIds: formData.printerIds,
        displayStatus: formData.displayStatus,
        availableTimeSlots: cleanedTimeSlots,
        allergens: formData.allergens,
        isSpicy: formData.isSpicy,
        isVegetarian: formData.isVegetarian,
        isAskPrice: formData.isAskPrice,
        costSettings: formData.costSettings.hasCost ? formData.costSettings : null,
        sortOrder: 0,
        isActive: formData.isActive,
        showOnReservationMenu: formData.showOnReservationMenu,
        assignedStaffIds: formData.assignedStaffIds,
        deliveryAvailable: formData.deliveryAvailable,
        deliveryPrice: formData.deliveryAvailable ? (Number(formData.deliveryPrice) || 0) : null,
        updatedAt: now,
      };

      if (editingProduct) {
        // 更新（sortOrderは変更しない）
        const { sortOrder, ...updateData } = productData;
        await updateDoc(doc(db, 'products', editingProduct.id), updateData);
      } else {
        // 新規作成（sortOrderは最大値+1）
        const maxSortOrder = products.length > 0
          ? Math.max(...products.map(p => p.sortOrder || 0))
          : 0;
        await addDoc(collection(db, 'products'), {
          ...productData,
          sortOrder: maxSortOrder + 1,
          createdAt: now,
        });
        setLastSavedCategoryId(formData.categoryId);
        handleCloseDialog();
        fetchData();
        setShowContinueDialog(true);
        return;
      }

      handleCloseDialog();
      fetchData();
      setSuccess('商品を更新しました');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('商品保存エラー:', error);
      setError('商品の保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'products', deleteTarget.id));
      setSuccess('商品を削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('商品削除エラー:', error);
      setError('商品の削除に失敗しました');
    }
  };

  const handleMoveSortOrder = async (product: Product, direction: 'up' | 'down') => {
    const currentIndex = products.findIndex(p => p.id === product.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === products.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetProduct = products[targetIndex];

    try {
      // 順序を入れ替え
      await updateDoc(doc(db, 'products', product.id), {
        sortOrder: targetProduct.sortOrder,
        updatedAt: Timestamp.now(),
      });
      await updateDoc(doc(db, 'products', targetProduct.id), {
        sortOrder: product.sortOrder,
        updatedAt: Timestamp.now(),
      });

      fetchData();
    } catch (error) {
      console.error('順序変更エラー:', error);
      setError('順序の変更に失敗しました');
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return t('uncategorized');
    return getLocalizedName(category, language);
  };

  const handleBatchTranslate = async () => {
    if (!shop || products.length === 0) return;

    // 50件以上の場合はバックグラウンド処理を提案
    const useBackground = products.length >= 50;
    const confirmMessage = useBackground
      ? `全${products.length}件の商品を一括翻訳しますか？\n` +
        `※大量のため、バックグラウンドで処理します\n` +
        `※翻訳完了は自動で反映されます（数分〜数十分）\n` +
        `※ページを閉じても処理は継続します`
      : `全${products.length}件の商品を一括翻訳しますか？\n` +
        `※既に翻訳済みの項目はスキップされます\n` +
        `※商品説明も翻訳されます\n` +
        `※処理には数分かかる場合があります`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setTranslating(true);
    setError(null);
    setTranslationProgress('翻訳準備中...');

    try {
      // 元言語はダッシュボード利用者の言語
      const sourceLanguage = language || 'ja';
      const allLanguages: ('ja' | 'en' | 'th' | 'zh-TW' | 'ko')[] = ['ja', 'en', 'th', 'zh-TW', 'ko'];
      const targetLanguages = allLanguages.filter(lang => lang !== sourceLanguage);

      const items = products.map(product => ({
        type: 'product',
        id: product.id,
        name: product.name,
        nameEn: product.nameEn,
        nameTh: product.nameTh,
        nameZhTw: product.nameZhTw,
        nameKo: product.nameKo,
        description: product.description,
        descriptionEn: product.descriptionEn,
        descriptionTh: product.descriptionTh,
        descriptionZhTw: product.descriptionZhTw,
        descriptionKo: product.descriptionKo,
        categoryId: product.categoryId,
      }));

      if (useBackground) {
        // バックグラウンド翻訳ジョブを作成
        setTranslationProgress('バックグラウンドジョブを作成中...');
        const createJobFn = httpsCallable(functions, 'createTranslationJob');
        const result = await createJobFn({
          shopId: shop.id,
          sourceLanguage,
          targetLanguages,
          items,
          collectionName: 'products',
        });

        const data = result.data as { jobId: string; message: string };
        setSuccess(
          `${data.message}\n翻訳完了後、自動で反映されます。`
        );
        setTimeout(() => setSuccess(null), 10000);

        // ジョブの進捗をポーリング（オプション）
        pollTranslationJobStatus(data.jobId);
      } else {
        // 50件未満は従来通り同期処理（タイムアウト10分）
        const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 600000 });

        setTranslationProgress(`翻訳中... (${items.length}件)`);

        const result = await translateFn({
          shopName: shop.shopName,
          businessTypeCode: shop.businessTypeCode,
          sourceLanguage,
          targetLanguages,
          items,
          batchSize: 20,
          forceRetranslate: true,
        });

        const data = result.data as { translations: any[]; skipped: number };

        setTranslationProgress('データベースを更新中...');

        // Batch Writeで一括更新
        const batch = writeBatch(db);
        let updateCount = 0;

        data.translations.forEach((translation: any) => {
          const productRef = doc(db, 'products', translation.id);
          const updateData: any = {
            updatedAt: Timestamp.now(),
          };

          // 注意: nameとdescriptionは元言語なので上書きしない
          // 翻訳先の言語フィールドのみ更新
          if (translation.nameEn) updateData.nameEn = translation.nameEn;
          if (translation.nameTh) updateData.nameTh = translation.nameTh;
          if (translation.nameZhTw) updateData.nameZhTw = translation.nameZhTw;
          if (translation.nameKo) updateData.nameKo = translation.nameKo;
          if (translation.nameJa) updateData.nameJa = translation.nameJa;

          if (translation.descriptionEn) updateData.descriptionEn = translation.descriptionEn;
          if (translation.descriptionTh) updateData.descriptionTh = translation.descriptionTh;
          if (translation.descriptionZhTw) updateData.descriptionZhTw = translation.descriptionZhTw;
          if (translation.descriptionKo) updateData.descriptionKo = translation.descriptionKo;
          if (translation.descriptionJa) updateData.descriptionJa = translation.descriptionJa;

          batch.update(productRef, updateData);
          updateCount++;
        });

        await batch.commit();

        setSuccess(
          `翻訳完了: ${updateCount}件を更新しました` +
          (data.skipped > 0 ? ` (${data.skipped}件はスキップ)` : '')
        );
        setTimeout(() => setSuccess(null), 5000);

        // データを再取得して画面を更新
        await fetchData();
      }
    } catch (error: any) {
      console.error('一括翻訳エラー:', error);
      setError(`翻訳に失敗しました: ${error.message || '不明なエラー'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTranslating(false);
      setTranslationProgress('');
    }
  };

  // バックグラウンドジョブの進捗をポーリング
  const pollTranslationJobStatus = async (jobId: string) => {
    const getStatusFn = httpsCallable(functions, 'getTranslationJobStatus');
    let attempts = 0;
    const maxAttempts = 60; // 最大10分（10秒×60回）

    const poll = async () => {
      try {
        const result = await getStatusFn({ jobId });
        const status = result.data as {
          status: string;
          totalItems: number;
          processedItems: number;
          failedItems: number;
          error?: string;
        };

        if (status.status === 'completed') {
          setSuccess(
            `バックグラウンド翻訳完了: ${status.processedItems}件を処理しました` +
            (status.failedItems > 0 ? ` (${status.failedItems}件失敗)` : '')
          );
          setTimeout(() => setSuccess(null), 8000);
          fetchData(); // データを再取得
          return;
        }

        if (status.status === 'failed') {
          setError(`バックグラウンド翻訳が失敗しました: ${status.error || '不明なエラー'}`);
          setTimeout(() => setError(null), 8000);
          return;
        }

        if (status.status === 'processing') {
          setTranslationProgress(
            `バックグラウンド翻訳中... (${status.processedItems}/${status.totalItems}件)`
          );
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // 10秒後に再ポーリング
        } else {
          setTranslationProgress('');
          setSuccess('バックグラウンドで翻訳を継続中です。完了後に自動で反映されます。');
          setTimeout(() => setSuccess(null), 5000);
        }
      } catch (error) {
        console.error('ポーリングエラー:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        }
      }
    };

    setTimeout(poll, 5000); // 5秒後に最初のポーリング
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.categoryId === selectedCategory);

  // 予約メニュー（menusコレクション）からの移行
  const handleMigrateMenus = async () => {
    if (!shop) return;

    const confirmed = window.confirm(
      '予約メニュー（旧形式）のデータを商品に移行します。\n' +
      '移行されたメニューは「予約メニューに表示」フラグがONになります。\n\n' +
      '実行しますか？'
    );
    if (!confirmed) return;

    setMigrating(true);
    setError(null);

    try {
      const migrateFn = httpsCallable(functions, 'migrateMenusToProducts', { timeout: 600000 });
      const result = await migrateFn({ shopId: shop.id, dryRun: false });
      const data = result.data as { success: boolean; message: string; migrated: number; skipped: number };

      if (data.success) {
        setSuccess(`移行完了: ${data.migrated}件移行, ${data.skipped}件スキップ`);
        // 商品一覧を再取得
        await fetchData();
      }
    } catch (err: any) {
      console.error('移行エラー:', err);
      setError(`移行エラー: ${err.message}`);
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  if (!shop) {
    return (
      <Container>
        <Alert severity="warning">
          店舗が選択されていません。店舗を選択してください。
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">商品管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleMigrateMenus}
            disabled={migrating}
          >
            {migrating ? '移行中...' : '予約メニューを移行'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={translating ? <span>⏳</span> : <TranslateIcon />}
            onClick={handleBatchTranslate}
            disabled={translating || products.length === 0}
          >
            {translating ? translationProgress : '一括翻訳 (AI)'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            商品追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {categories.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>カテゴリが未登録です</AlertTitle>
          商品を登録する前に、まずカテゴリを作成してください。
          <Button
            size="small"
            variant="outlined"
            sx={{ ml: 2 }}
            onClick={() => navigate('/mobile-order/categories')}
          >
            カテゴリを作成する
          </Button>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>カテゴリで絞り込み</InputLabel>
          <Select
            value={selectedCategory}
            label="カテゴリで絞り込み"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">すべて</MenuItem>
            {categories.map(category => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        {filteredProducts.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                商品が登録されていません
              </Typography>
            </Paper>
          </Grid>
        ) : (
          filteredProducts.map((product, index) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card>
                {shop?.displaySettings?.showProductImages !== false && (
                  <>
                    {product.imageUrl ? (
                      product.mediaType === 'video' ? (
                        <CardMedia
                          component="video"
                          height="140"
                          src={product.imageUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <CardMedia
                          component="img"
                          height="140"
                          image={product.imageUrl}
                          alt={product.name}
                        />
                      )
                    ) : (
                      <CardMedia
                        component="img"
                        height="140"
                        image="/assets/no-photo.svg"
                        alt="画像なし"
                        sx={{ opacity: 0.5 }}
                      />
                    )}
                  </>
                )}
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {product.code && (
                      <Typography component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 1 }}>
                        [{categories.find(c => c.id === product.categoryId)?.code || ''}{categories.find(c => c.id === product.categoryId)?.code && product.code ? '-' : ''}{product.code}]
                      </Typography>
                    )}
                    {getLocalizedName(product, language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {getLocalizedDescription(product, language) || t('noDescription')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip label={getCategoryName(product.categoryId)} size="small" />
                    {product.showOnReservationMenu && <Chip label="予約メニュー" size="small" color="info" />}
                    {product.assignedStaffIds && product.assignedStaffIds.length > 0 && (
                      <Chip
                        label={`担当: ${product.assignedStaffIds.map(id => staffMembers.find(s => s.id === id)?.name || '').filter(Boolean).join(', ')}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                    {product.isSpicy && <Chip label={`🌶️ ${t('spicy')}`} size="small" color="error" />}
                    {product.isVegetarian && <Chip label={`🥗 ${t('vegetarian')}`} size="small" color="success" />}
                    {product.displayStatus === 'soldout' && <Chip label={t('soldOut')} size="small" color="warning" />}
                    {product.displayStatus === 'hidden' && <Chip label={t('hidden')} size="small" color="default" />}
                  </Box>
                  <Typography variant="h6" color="primary">
                    ¥{(product.price || 0).toLocaleString()}
                  </Typography>
                  {product.optionIds && product.optionIds.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('optionCount')}: {product.optionIds.length}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSortOrder(product, 'up')}
                        disabled={filteredProducts.findIndex(p => p.id === product.id) === 0}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSortOrder(product, 'down')}
                        disabled={filteredProducts.findIndex(p => p.id === product.id) === filteredProducts.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(product)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteTarget(product);
                          setOpenDeleteDialog(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* 編集/追加ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? '商品編集' : '商品追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>カテゴリ</InputLabel>
              <Select
                value={formData.categoryId}
                label="カテゴリ"
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              >
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>基本情報</Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="商品コード"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder={formData.categoryId ? generateProductCode(formData.categoryId) : '001'}
                helperText="空欄なら自動採番"
                sx={{ width: 150 }}
                inputProps={{ maxLength: 10 }}
              />
              <TextField
                label="商品名"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                placeholder="例: ラーメン"
              />
            </Box>

            {/* 多言語入力 */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="英語 (EN)"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="Ramen"
              />
              <TextField
                label="タイ語 (TH)"
                value={formData.nameTh}
                onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="ราเมน"
              />
              <TextField
                label="中国語 (ZH)"
                value={formData.nameZhTw}
                onChange={(e) => setFormData({ ...formData, nameZhTw: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="拉面"
              />
              <TextField
                label="韓国語 (KO)"
                value={formData.nameKo}
                onChange={(e) => setFormData({ ...formData, nameKo: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
                placeholder="라멘"
              />
            </Box>

            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <MediaUpload
              currentMediaUrl={formData.imageUrl}
              currentMediaType={formData.mediaType}
              onMediaUploaded={(url, type) => setFormData({ ...formData, imageUrl: url, mediaType: type })}
              onMediaDeleted={() => setFormData({ ...formData, imageUrl: '', mediaType: 'image' })}
              storagePath={`products/${editingProduct?.id || 'new'}`}
              label="商品メディア"
            />

            <TextField
              label="価格"
              value={formData.price}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setFormData({ ...formData, price: value });
                }
              }}
              required
              fullWidth
              placeholder="0"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>¥</Typography>
              }}
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>オプション設定</Typography>

            <FormControl fullWidth>
              <InputLabel>オプション（複数選択可）</InputLabel>
              <Select
                multiple
                value={formData.optionIds}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const value = e.target.value;
                  setFormData({ ...formData, optionIds: typeof value === 'string' ? value.split(',') : value });
                }}
                input={<OutlinedInput label="オプション（複数選択可）" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const option = options.find(o => o.id === value);
                      return <Chip key={value} label={option?.name} size="small" />;
                    })}
                  </Box>
                )}
              >
                {options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name} ({option.type === 'single' ? '単一選択' : '複数選択'})
                    {option.required && ' - 必須'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>表示ステータス</Typography>

            <ToggleButtonGroup
              value={formData.displayStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setFormData({ ...formData, displayStatus: newValue });
                }
              }}
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="available" color="success">
                販売中
              </ToggleButton>
              <ToggleButton value="soldout" color="warning">
                売り切れ
              </ToggleButton>
              <ToggleButton value="hidden" color="standard">
                非表示
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>表示設定</Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showOnReservationMenu}
                    onChange={(e) => setFormData({ ...formData, showOnReservationMenu: e.target.checked })}
                  />
                }
                label="予約メニューに表示"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
              ONにすると、お客様が予約時にこの商品を選択できるようになります
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.deliveryAvailable}
                    onChange={(e) => setFormData({ ...formData, deliveryAvailable: e.target.checked })}
                  />
                }
                label="出前（デリバリー）対応"
              />
            </Box>
            {formData.deliveryAvailable && (
              <TextField
                label="デリバリー価格"
                type="number"
                fullWidth
                value={formData.deliveryPrice}
                onChange={(e) => setFormData({ ...formData, deliveryPrice: e.target.value })}
                helperText="店内価格と異なる場合に設定してください（空欄の場合は通常価格が適用されます）"
                sx={{ mt: 1 }}
              />
            )}

            {/* 予約メニューに表示する場合のみ、担当者選択を表示 */}
            {formData.showOnReservationMenu && staffMembers.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>担当スタッフ</Typography>
                <FormControl fullWidth>
                  <InputLabel>担当スタッフ（複数選択可）</InputLabel>
                  <Select
                    multiple
                    value={formData.assignedStaffIds}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const value = e.target.value;
                      setFormData({ ...formData, assignedStaffIds: typeof value === 'string' ? value.split(',') : value });
                    }}
                    input={<OutlinedInput label="担当スタッフ（複数選択可）" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const staff = staffMembers.find(s => s.id === value);
                          return <Chip key={value} label={staff?.name} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {staffMembers.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  選択しない場合は全スタッフが担当可能になります
                </Typography>
              </>
            )}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>その他</Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isSpicy}
                    onChange={(e) => setFormData({ ...formData, isSpicy: e.target.checked })}
                  />
                }
                label="辛い商品"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVegetarian}
                    onChange={(e) => setFormData({ ...formData, isVegetarian: e.target.checked })}
                  />
                }
                label="ベジタリアン対応"
              />
            </Box>

            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">時間帯設定（詳細）</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    この商品を特定の時間帯のみ表示したい場合に設定してください。
                    設定しない場合は常時表示されます。
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel>表示タイプ</InputLabel>
                    <Select
                      value={formData.availableTimeSlots.type}
                      label="表示タイプ"
                      onChange={(e) => setFormData({
                        ...formData,
                        availableTimeSlots: {
                          type: e.target.value as 'always' | 'specific_times',
                          timeSlots: e.target.value === 'always' ? [] : formData.availableTimeSlots.timeSlots || []
                        }
                      })}
                    >
                      <MenuItem value="always">常時表示</MenuItem>
                      <MenuItem value="specific_times">特定時間のみ表示</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.availableTimeSlots.type === 'specific_times' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2">時間帯</Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const currentSlots = formData.availableTimeSlots.timeSlots || [];
                            setFormData({
                              ...formData,
                              availableTimeSlots: {
                                ...formData.availableTimeSlots,
                                timeSlots: [
                                  ...currentSlots,
                                  { start: '11:00', end: '14:00', name: '' }
                                ]
                              }
                            });
                          }}
                        >
                          時間帯を追加
                        </Button>
                      </Box>

                      {(formData.availableTimeSlots.timeSlots || []).map((slot, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                          <TextField
                            label="名前（任意）"
                            value={slot.name || ''}
                            onChange={(e) => {
                              const newSlots = [...(formData.availableTimeSlots.timeSlots || [])];
                              newSlots[index] = { ...slot, name: e.target.value };
                              setFormData({
                                ...formData,
                                availableTimeSlots: {
                                  ...formData.availableTimeSlots,
                                  timeSlots: newSlots
                                }
                              });
                            }}
                            size="small"
                            placeholder="例: ランチ"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="開始時刻"
                            type="time"
                            value={slot.start}
                            onChange={(e) => {
                              const newSlots = [...(formData.availableTimeSlots.timeSlots || [])];
                              newSlots[index] = { ...slot, start: e.target.value };
                              setFormData({
                                ...formData,
                                availableTimeSlots: {
                                  ...formData.availableTimeSlots,
                                  timeSlots: newSlots
                                }
                              });
                            }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="終了時刻"
                            type="time"
                            value={slot.end}
                            onChange={(e) => {
                              const newSlots = [...(formData.availableTimeSlots.timeSlots || [])];
                              newSlots[index] = { ...slot, end: e.target.value };
                              setFormData({
                                ...formData,
                                availableTimeSlots: {
                                  ...formData.availableTimeSlots,
                                  timeSlots: newSlots
                                }
                              });
                            }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const newSlots = (formData.availableTimeSlots.timeSlots || []).filter((_, i) => i !== index);
                              setFormData({
                                ...formData,
                                availableTimeSlots: {
                                  ...formData.availableTimeSlots,
                                  timeSlots: newSlots
                                }
                              });
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}

                      {(!formData.availableTimeSlots.timeSlots || formData.availableTimeSlots.timeSlots.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          「時間帯を追加」ボタンから時間帯を設定してください
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ASK商品設定 */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">ASK商品（価格後入力）</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    注文時または会計時に価格を入力する商品の場合にONにしてください。
                    価格は0円として登録され、注文時にスタッフが価格を入力できます。
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isAskPrice}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            isAskPrice: e.target.checked,
                            price: e.target.checked ? '' : formData.price,
                          });
                        }}
                      />
                    }
                    label="ASK商品（価格後入力）"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* 原価・バック設定 */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">原価・コミッション設定</Typography>
              </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      この商品の原価とスタッフへのバック率を設定します。
                      バック金額 = (販売価格 - 原価) × バック率
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.costSettings.hasCost}
                          onChange={(e) => setFormData({
                            ...formData,
                            costSettings: {
                              ...formData.costSettings,
                              hasCost: e.target.checked,
                            }
                          })}
                        />
                      }
                      label="原価管理対象"
                    />
                    {formData.costSettings.hasCost && (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="商品原価"
                            value={formData.costSettings.cost || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setFormData({
                                  ...formData,
                                  costSettings: {
                                    ...formData.costSettings,
                                    cost: value === '' ? 0 : Number(value),
                                  }
                                });
                              }
                            }}
                            placeholder="0"
                            InputProps={{
                              endAdornment: <Typography variant="body2">{shop?.currency || 'JPY'}</Typography>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="バック率"
                            value={formData.costSettings.backRate ? Math.round(formData.costSettings.backRate * 100) : ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setFormData({
                                  ...formData,
                                  costSettings: {
                                    ...formData.costSettings,
                                    backRate: value === '' ? 0 : pctToRate(value),
                                  }
                                });
                              }
                            }}
                            placeholder="0"
                            InputProps={{
                              endAdornment: <Typography variant="body2">%</Typography>,
                            }}
                            helperText={`予想バック金額: ${((Number(formData.price) - formData.costSettings.cost) * formData.costSettings.backRate).toLocaleString()} ${shop?.currency || 'JPY'}`}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={categories.length === 0}>
            {editingProduct ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>商品を削除</DialogTitle>
        <DialogContent>
          <Typography>
            商品「{deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 商品追加成功 → 続けて登録するか確認ダイアログ */}
      <Dialog open={showContinueDialog} onClose={() => setShowContinueDialog(false)}>
        <DialogTitle>商品を追加しました</DialogTitle>
        <DialogContent>
          <Typography>
            同じカテゴリ「{categories.find(c => c.id === lastSavedCategoryId)?.name}」で続けて商品を登録しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContinueDialog(false)}>
            閉じる
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowContinueDialog(false);
              setEditingProduct(null);
              setFormData({
                categoryId: lastSavedCategoryId,
                code: '',
                name: '',
                nameEn: '',
                nameTh: '',
                nameZhTw: '',
                nameKo: '',
                description: '',
                descriptionEn: '',
                descriptionTh: '',
                descriptionZhTw: '',
                descriptionKo: '',
                price: '',
                imageUrl: '',
                mediaType: 'image' as 'image' | 'video',
                optionIds: [],
                printerIds: [],
                displayStatus: 'available',
                availableTimeSlots: {
                  type: 'always',
                  timeSlots: [],
                },
                allergens: [],
                isSpicy: false,
                isVegetarian: false,
                isAskPrice: false,
                costSettings: {
                  hasCost: false,
                  cost: 0,
                  backRate: shop?.costBackSettings?.defaultBackRate || 0.5,
                },
                isActive: true,
                showOnReservationMenu: false,
                assignedStaffIds: [],
                deliveryAvailable: false,
                deliveryPrice: '',
              });
              setOpenDialog(true);
            }}
          >
            同じカテゴリで続けて登録
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Products;
